import React, { useMemo, useState, useCallback, useEffect } from 'react';
/* useNavigate removed: booking links now point to external BR OS app */
import { AlertTriangle, Loader2, ArrowUpDown, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Badge } from './ui/badge';
import { formatCurrency } from '../lib/utils';
import { diagnosticsAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

// Sortable column header
function SortableColumnHead(props) {
  const { field, sortField, sortAsc, onToggle, children, className } = props;
  return (
    <TableHead
      className={`cursor-pointer select-none hover:text-foreground ${className || ''}`}
      onClick={() => onToggle(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortField === field && (
          <ArrowUpDown className="h-3 w-3" />
        )}
      </span>
    </TableHead>
  );
}

/**
 * Shared drill-down modal for Billing vs Sales chart.
 * Handles both bookings and invoiced series.
 */
// Props destructured in body, not params: workaround for Emergent babel-metadata-plugin crash (line 936). Do not convert to function({...}).
export function BillingDrilldownModal(props) {
  const { open, onClose, month, series, barValue, dc } = props;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [sortField, setSortField] = useState(
    series === 'bookings' ? 'created_at' : series === 'pipeline' ? 'check_in' : 'issue_date'
  );
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    if (!open || !month || !series) return;
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setItems([]);
      setMeta(null);
      setSortField(series === 'bookings' ? 'created_at' : series === 'pipeline' ? 'check_in' : 'issue_date');
      setSortAsc(false);
      try {
        const { data } = await diagnosticsAPI.billingVsSalesContents({
          month,
          series,
          currency: (dc || 'GBP').toLowerCase(),
        });
        if (cancelled) return;
        setItems(data.items || []);
        setMeta(data);
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [open, month, series, dc]);

  const toggleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortAsc((a) => !a);
        return field;
      }
      setSortAsc(field === 'bros_id');
      return field;
    });
  }, []);

  const sorted = useMemo(() => {
    if (!items.length) return [];
    return [...items].sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va == null) va = '';
      if (vb == null) vb = '';
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [items, sortField, sortAsc]);

  // Sanity check: modal sum vs chart bar value
  const modalSum = meta?.total_in_display_currency ?? null;
  const sumMismatch = useMemo(() => {
    if (modalSum === null || barValue == null) return false;
    const diff = Math.abs(modalSum - barValue);
    const maxVal = Math.max(Math.abs(modalSum), Math.abs(barValue), 1);
    return diff / maxVal > 0.001;
  }, [modalSum, barValue]);

  const sharedHeadProps = { sortField, sortAsc, onToggle: toggleSort };
  const isBookings = series === 'bookings';
  const isPipeline = series === 'pipeline';
  const isBookingsLike = isBookings || isPipeline;
  const title = isPipeline ? 'Pipeline (Open Enquiries)' : isBookings ? 'Bookings' : 'Invoiced';
  const monthLabel = month || '';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {title} ({monthLabel}) ({meta?.count ?? 0} records, {formatCurrency(modalSum ?? barValue ?? 0, dc)})
          </DialogTitle>
          <DialogDescription>
            {isPipeline
              ? 'Open enquiries with check-in in this month. Click a record to probe it.'
              : isBookings
                ? 'Bookings attributed to this month by creation date. Click a record to probe it.'
                : 'Invoices attributed to this month by issue date. Click a record to probe it.'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && sorted.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">No records for this month.</p>
        )}

        {!loading && sorted.length > 0 && (
          <>
            {sumMismatch && (
              <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-700 rounded p-2 bg-amber-50 dark:bg-amber-950/20">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Sum mismatch: modal total {formatCurrency(modalSum, dc)} vs chart bar {formatCurrency(barValue, dc)}.
                This divergence is itself diagnostic.
              </div>
            )}

            {!isBookingsLike && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5 shrink-0" />
                Voided invoices excluded from Invoiced series.
              </div>
            )}

            {isPipeline && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5 shrink-0" />
                Open enquiries shown at full value, not probability-weighted.
              </div>
            )}

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableColumnHead field="bros_id" {...sharedHeadProps}>
                      {isBookingsLike ? 'Booking' : 'Invoice'}
                    </SortableColumnHead>
                    <SortableColumnHead field="company" {...sharedHeadProps}>Client</SortableColumnHead>
                    {isBookingsLike ? (
                      <>
                        <SortableColumnHead field="created_at" {...sharedHeadProps}>Created</SortableColumnHead>
                        <SortableColumnHead field="check_in" {...sharedHeadProps}>Check-in</SortableColumnHead>
                        <SortableColumnHead field="check_out" {...sharedHeadProps}>Check-out</SortableColumnHead>
                      </>
                    ) : (
                      <>
                        <SortableColumnHead field="issue_date" {...sharedHeadProps}>Issue Date</SortableColumnHead>
                        <SortableColumnHead field="due_date" {...sharedHeadProps}>Due Date</SortableColumnHead>
                      </>
                    )}
                    <SortableColumnHead field="status" {...sharedHeadProps}>Status</SortableColumnHead>
                    <SortableColumnHead field="amount_in_display_currency" className="text-right" {...sharedHeadProps}>
                      Amount ({dc})
                    </SortableColumnHead>
                    <TableHead className="text-right">Native</TableHead>
                    {isBookings && <TableHead className="text-right">Invoices</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((item, idx) => (
                    <TableRow key={item.bros_id || idx}>
                      <TableCell className="font-mono">
                        <a
                          href={`https://internal.bonjour-residences.co.uk/bookings/${encodeURIComponent(item.bros_id)}`}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="recon-booking-link"
                        >
                          {item.bros_id || '(no id)'}
                        </a>
                      </TableCell>
                      <TableCell className="text-sm truncate max-w-[160px]" title={item.company}>
                        {item.company || '(unknown)'}
                      </TableCell>
                      {isBookingsLike ? (
                        <>
                          <TableCell className="text-sm">{(item.created_at || '').slice(0, 10) || 'N/A'}</TableCell>
                          <TableCell className="text-sm">{(item.check_in || '').slice(0, 10) || 'N/A'}</TableCell>
                          <TableCell className="text-sm">{(item.check_out || '').slice(0, 10) || 'N/A'}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-sm">{item.issue_date || 'N/A'}</TableCell>
                          <TableCell className="text-sm">{item.due_date || 'N/A'}</TableCell>
                        </>
                      )}
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{item.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(item.amount_in_display_currency, dc)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground text-xs">
                        {formatCurrency(item.amount_native, item.currency)} {item.currency}
                      </TableCell>
                      {isBookings && (
                        <TableCell className="text-right">
                          {item.invoice_count > 1 ? (
                            <Badge variant="destructive" className="text-xs">{item.invoice_count}</Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">{item.invoice_count}</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between text-sm text-muted-foreground pt-2 border-t">
              <span>{meta?.count ?? sorted.length} record(s)</span>
              <span className="font-mono font-medium text-foreground">
                Total: {formatCurrency(modalSum ?? 0, dc)}
              </span>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
